import { type HTMLAttributes, type ReactNode } from "react";
import type { HumanInterrupt, HumanResponse } from "../humanInterrupt";
import {
  InterruptCardProvider,
  useInterruptCard,
} from "./InterruptCardContext";
import { Header } from "./parts/Header";
import { Description } from "./parts/Description";
import { Args } from "./parts/Args";
import { Actions } from "./parts/Actions";
import { Approve } from "./parts/Approve";
import { Reject } from "./parts/Reject";
import { Respond } from "./parts/Respond";

/**
 * Compound component family for rendering a paused-graph interrupt
 * inline in a chat stream. The agent called LangGraph's `interrupt()`,
 * the runtime paused, and the consumer's UI now needs to collect a
 * `HumanResponse` and resume.
 *
 * The Card is a dumb shell. It composes named children:
 *
 *   <InterruptCard interrupt={...} onResponse={...}>
 *     <InterruptCard.Header />        // title || humanize(action)
 *     <InterruptCard.Description />   // optional body prose
 *     <InterruptCard.Args />          // generic args dump (consumers usually replace)
 *     <InterruptCard.Actions>
 *       <InterruptCard.Approve />     // accept
 *       <InterruptCard.Reject />      // ignore
 *       <InterruptCard.Respond />     // response (free-text)
 *     </InterruptCard.Actions>
 *   </InterruptCard>
 *
 * Consumers replace `<Args />` with their own children for tool-specific
 * rendering — e.g. the editCustomField interrupt wants a per-site/page
 * breakdown table, not a flat key/value dump. The compound shape stays
 * the same; only the body changes.
 *
 * Buttons gate themselves on `interrupt.config.allow_*`. Including all
 * three children unconditionally is fine — only the allowed ones surface.
 *
 * Transport is the consumer's responsibility. `onResponse` typically
 * wraps a LangGraph SDK call:
 *
 *   onResponse={(response) =>
 *     client.runs.stream(threadId, graphId, {
 *       command: { resume: response },
 *       streamMode: "events",
 *     })
 *   }
 *
 * Unstyled-friendly: every part takes className overrides.
 */
export interface InterruptCardProps extends HTMLAttributes<HTMLDivElement> {
  interrupt: HumanInterrupt;
  onResponse: (response: HumanResponse) => Promise<void>;
  children: ReactNode;
}

function InterruptCardRoot({
  interrupt,
  onResponse,
  children,
  ...props
}: InterruptCardProps) {
  return (
    <InterruptCardProvider interrupt={interrupt} onResponse={onResponse}>
      <div
        {...props}
        className={[
          "rounded-lg border border-zinc-200 bg-white p-4",
          "flex flex-col gap-3",
          props.className,
        ]
          .filter(Boolean)
          .join(" ")}
        role="region"
        aria-label={interrupt.title ?? interrupt.action_request.action}
      >
        {children}
        <ErrorBanner />
      </div>
    </InterruptCardProvider>
  );
}

/** Inline error display for failed submits — surfaces inside the card. */
function ErrorBanner() {
  const { error } = useInterruptCard();
  if (!error) return null;
  return (
    <div
      role="alert"
      className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-2 py-1"
    >
      {error}
    </div>
  );
}

// Compound namespace export — `<InterruptCard.Header />` etc.
export const InterruptCard = Object.assign(InterruptCardRoot, {
  Header,
  Description,
  Args,
  Actions,
  Approve,
  Reject,
  Respond,
});
